import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { stageStyle } from "@/lib/pgt";
import { LANG_LABEL } from "@/lib/pgt";
import { Checkbox } from "@/components/ui/checkbox";
import { Phone, Building2, AlertTriangle, Clock, MessageCircleReply, Pencil, Sparkles, Trash2 } from "lucide-react";

export default function CustomerCard({ customer, note, testId, onEdit, onDelete, onToggleSelect, selected = false, highlightIdle = false, idleDays = 7 }) {
  const navigate = useNavigate();
  if (!customer) return null;
  const c = customer;
  const go = () => navigate(`/customers/${c.id}`);
  const isUncontacted = highlightIdle && c.never_contacted;
  const isStale = highlightIdle && !c.never_contacted && (c.days_since_message ?? 0) >= idleDays;
  const showNudge = isUncontacted || isStale;
  const messageNow = (e) => {
    e.stopPropagation();
    navigate(`/customers/${c.id}?tab=copilot&draft=1`);
  };

  return (
    <div
      onClick={go}
      onKeyDown={(e) => { if (e.key === "Enter") go(); }}
      role="button"
      tabIndex={0}
      data-testid={testId || `customer-card-${c.id}`}
      className={`group relative w-full cursor-pointer text-left rounded-xl border bg-white p-4 transition-all hover:shadow-md ${
        selected ? "border-red-300 ring-2 ring-red-200 bg-red-50/30"
          : isUncontacted ? "border-amber-300 ring-1 ring-amber-300 hover:border-amber-400" : "border-stone-200 hover:border-forest/40"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
        {onToggleSelect && (
          <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} className="pt-1">
            <Checkbox
              checked={selected}
              onCheckedChange={() => onToggleSelect(c.id)}
              aria-label={`Select ${c.name}`}
              data-testid={`customer-select-${c.id}`}
              className="rounded-[4px] border-stone-300 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
            />
          </span>
        )}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading font-semibold text-stone-900 truncate">{c.name}</span>
            {c.overdue && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600">
                <AlertTriangle className="h-3 w-3" /> OVERDUE
              </span>
            )}
            {isUncontacted && (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`badge-uncontacted-${c.id}`}>
                Not contacted
              </span>
            )}
            {isStale && (
              <span className="inline-flex items-center rounded-full bg-orange-50 px-1.5 py-0.5 text-[10px] font-semibold text-orange-600" data-testid={`badge-stale-${c.id}`}>
                {c.days_since_message}d no contact
              </span>
            )}
          </div>
          {c.company && (
            <div className="mt-0.5 flex items-center gap-1 text-xs text-stone-500 truncate">
              <Building2 className="h-3 w-3" /> {c.company}
            </div>
          )}
          <div className="mt-1 flex items-center gap-1 text-xs text-stone-500">
            <Phone className="h-3 w-3" /> {c.mobile}
          </div>
        </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {(onEdit || onDelete) && (
            <div className="flex items-center gap-0.5">
              {onEdit && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onEdit(c); }}
                  data-testid={`customer-edit-${c.id}`}
                  aria-label="Edit customer"
                  className="rounded-md p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-forest"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDelete(c); }}
                  data-testid={`customer-delete-${c.id}`}
                  aria-label="Delete customer"
                  className="rounded-md p-1 text-stone-400 transition-colors hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
          <Badge variant="outline" className={`text-[10px] ${stageStyle(c.stage)}`}>
            {c.stage_short || c.stage_label}
          </Badge>
          <span className="text-[10px] font-medium text-stone-400">{LANG_LABEL[c.preferred_language]}</span>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-stone-500">
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {c.days_since_last != null ? `${c.days_since_last}d since contact` : "no contact yet"}
        </span>
        {c.unanswered_followups > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
            <MessageCircleReply className="h-3 w-3" />
            {c.unanswered_followups} unanswered
          </span>
        )}
        {c.assigned_name && <span className="text-stone-400">· {c.assigned_name}</span>}
      </div>

      {showNudge && (
        <button
          onClick={messageNow}
          data-testid={`message-now-${c.id}`}
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-forest px-2.5 py-1.5 text-xs font-semibold text-ivory transition-colors hover:bg-forest-600"
        >
          <Sparkles className="h-3.5 w-3.5" /> Message now
        </button>
      )}

      {note && <div className="mt-2 text-xs text-forest font-medium">{note}</div>}
    </div>
  );
}
